import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import { useToast } from './Toast';
import { 
  Employee, 
  Location, 
  Position, 
  Schedule, 
  PayRecord, 
  PaymentType, 
  PaymentFrequency, 
  AssignLaborCostByOption, 
  WorkingDay,
  CreatePayRecordRequest,
  UpdatePayRecordRequest,
  PayRateMatrix,
  CreatePayRateMatrixRequest
} from '../types';
import { 
  payRecordApi, 
  paymentTypeApi, 
  paymentFrequencyApi, 
  assignLaborCostByOptionApi, 
  workingDayApi,
  companySettingsApi
} from '../services/api';
import { CompanySettings } from '../types';

interface PayrollAndPayRecordsProps {
  employee: Employee;
  payRecords: PayRecord[];
  locations: Location[];
  positions: Position[];
  schedules: Schedule[];
  onRefresh: () => void;
}

const PayrollAndPayRecords: React.FC<PayrollAndPayRecordsProps> = ({
  employee,
  payRecords,
  locations,
  positions,
  schedules,
  onRefresh
}) => {
  const toast = useToast();
  const [showPayrollModal, setShowPayrollModal] = useState(false);
  const [showPayRecordModal, setShowPayRecordModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PayRecord | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Lookup data
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [paymentFrequencies, setPaymentFrequencies] = useState<PaymentFrequency[]>([]);
  const [assignLaborCostByOptions, setAssignLaborCostByOptions] = useState<AssignLaborCostByOption[]>([]);
  const [workingDays, setWorkingDays] = useState<WorkingDay[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  
  // Payroll form data
  const [payrollData, setPayrollData] = useState({
    worksNumber: employee.payroll?.worksNumber || '',
    ppsNumber: employee.payroll?.ppsNumber || '',
    ban: employee.payroll?.ban || '',
    bic: employee.payroll?.bic || '',
    accountNumber: employee.payroll?.accountNumber || '',
    sortCode: employee.payroll?.sortCode || '',
  });
  
  // Pay Record form data
  const [formData, setFormData] = useState<CreatePayRecordRequest>({
    employeeId: employee.id,
    locationId: employee.profile?.mainLocationId || 0,
    positionId: employee.profile?.mainPositionId || 0,
    scheduleId: 0,
    paymentTypeId: 0, // Will be set when payment types are loaded
    contractedHours: 0,
    baseRate: 0,
    overtimeEligible: false,
    isActive: false,
  });
  
  const [selectedWorkingDayIds, setSelectedWorkingDayIds] = useState<number[]>([]);
  const [payRateMatrices, setPayRateMatrices] = useState<CreatePayRateMatrixRequest[]>([]);

  // Get employee's main and sub locations/positions
  const employeeLocations = [
    ...(employee.profile?.mainLocationId ? [locations.find(l => l.id === employee.profile!.mainLocationId!)] : []),
    ...(employee.profile?.subLocationIds?.map(id => locations.find(l => l.id === id)) || [])
  ].filter(Boolean) as Location[];

  const employeePositions = [
    ...(employee.profile?.mainPositionId ? [positions.find(p => p.id === employee.profile!.mainPositionId!)] : []),
    ...(employee.profile?.subPositionIds?.map(id => positions.find(p => p.id === id)) || [])
  ].filter(Boolean) as Position[];

  const employeeSchedules = (employee.profile?.scheduleIds?.map(id => schedules.find(s => s.id === id)) || [])
    .filter(Boolean) as Schedule[];

  useEffect(() => {
    const loadLookupData = async () => {
      try {
        const [types, frequencies, alcboOptions, wDays, companySettingsData] = await Promise.all([
          paymentTypeApi.getAll(),
          paymentFrequencyApi.getAll(),
          assignLaborCostByOptionApi.getAll(),
          workingDayApi.getAll(),
          companySettingsApi.get().catch(() => null)
        ]);
        setPaymentTypes(types);
        setPaymentFrequencies(frequencies);
        setAssignLaborCostByOptions(alcboOptions);
        setWorkingDays(wDays);
        setCompanySettings(companySettingsData);
        
        // Set default payment type to first available (or Hourly if exists)
        if (types.length > 0 && formData.paymentTypeId === 0) {
          const hourlyType = types.find(pt => pt.name === 'Hourly');
          const defaultType = hourlyType || types[0];
          setFormData(prev => ({ ...prev, paymentTypeId: defaultType.id }));
        }
      } catch (error) {
        console.error('Error loading lookup data:', error);
      }
    };
    loadLookupData();
  }, []);

  // Set default payment frequency based on payment type
  useEffect(() => {
    if (formData.paymentTypeId) {
      const paymentType = paymentTypes.find(pt => pt.id === formData.paymentTypeId);
      if (paymentType) {
        // For Hourly and Daily, default to Weekly frequency
        if (paymentType.name === 'Hourly' || paymentType.name === 'Daily') {
          const weeklyFreq = paymentFrequencies.find(pf => pf.name === 'Weekly');
          if (weeklyFreq) {
            setFormData({ ...formData, paymentFrequencyId: weeklyFreq.id });
          }
        } 
        // For Salary, default to Monthly frequency
        else if (paymentType.name === 'Salary') {
          const monthlyFreq = paymentFrequencies.find(pf => pf.name === 'Monthly');
          if (monthlyFreq) {
            setFormData({ ...formData, paymentFrequencyId: monthlyFreq.id });
          }
        }
        // Project-based payments default to "Contract" if available
        else if (paymentType.name === 'Project') {
          const contractFreq = paymentFrequencies.find(pf => pf.name === 'Contract');
          const monthlyInvoiceFreq = paymentFrequencies.find(pf => pf.name === 'Monthly Invoice');
          // If current frequency is not a Project-specific frequency, reset to Contract
          const currentFreq = paymentFrequencies.find(pf => pf.id === formData.paymentFrequencyId);
          if (currentFreq && currentFreq.name !== 'Contract' && currentFreq.name !== 'Monthly Invoice') {
            if (contractFreq) {
              setFormData({ ...formData, paymentFrequencyId: contractFreq.id });
            }
          } else if (!formData.paymentFrequencyId && contractFreq) {
            setFormData({ ...formData, paymentFrequencyId: contractFreq.id });
          }
        } else {
          // When switching away from Project, if current frequency is Project-specific, clear it
          const currentFreq = paymentFrequencies.find(pf => pf.id === formData.paymentFrequencyId);
          if (currentFreq && (currentFreq.name === 'Contract' || currentFreq.name === 'Monthly Invoice')) {
            setFormData({ ...formData, paymentFrequencyId: undefined });
          }
        }
      }
    }
  }, [formData.paymentTypeId, paymentTypes, paymentFrequencies]);

  // Set default location and position for new records (always set defaults, not just for active)
  useEffect(() => {
    // Set defaults when opening modal for new record (when editingRecord is null and form is reset)
    if (!editingRecord && (!formData.locationId || formData.locationId === 0) && employee.profile?.mainLocationId) {
      setFormData(prev => ({ ...prev, locationId: employee.profile!.mainLocationId! }));
    }
    if (!editingRecord && (!formData.positionId || formData.positionId === 0) && employee.profile?.mainPositionId) {
      setFormData(prev => ({ ...prev, positionId: employee.profile!.mainPositionId! }));
    }
  }, [editingRecord, employee.profile]);

  // Set default OT values from company settings when overtime is enabled
  useEffect(() => {
    if (formData.overtimeEligible && companySettings) {
      // Only set defaults if values are not already set (for new records or when enabling overtime)
      const updates: Partial<CreatePayRecordRequest> = {};
      
      if (!formData.ot1TriggerHours && companySettings.otRate1) {
        updates.ot1TriggerHours = companySettings.otRate1;
      }
      if (!formData.ot2TriggerHours && companySettings.otRate2) {
        updates.ot2TriggerHours = companySettings.otRate2;
      }
      if (!formData.ot1RateMultiplier && companySettings.ot1RateMultiplier) {
        updates.ot1RateMultiplier = companySettings.ot1RateMultiplier;
      }
      if (!formData.ot2RateMultiplier && companySettings.ot2RateMultiplier) {
        updates.ot2RateMultiplier = companySettings.ot2RateMultiplier;
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  }, [formData.overtimeEligible, companySettings, formData.ot1TriggerHours, formData.ot2TriggerHours, formData.ot1RateMultiplier, formData.ot2RateMultiplier]);

  const handlePayrollSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Payroll is saved through the main employee form
    // This is just for display/editing in the modal
    setShowPayrollModal(false);
    toast.success('Payroll information updated. Please save changes using the main form.');
  };

  const handlePayRecordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (saving) return;
    
    try {
      setSaving(true);
      
      // Validate required fields
      if (!formData.locationId || formData.locationId === 0) {
        toast.error('Location is required');
        setSaving(false);
        return;
      }
      if (!formData.positionId || formData.positionId === 0) {
        toast.error('Position is required');
        setSaving(false);
        return;
      }
      if (!formData.scheduleId || formData.scheduleId === 0) {
        toast.error('Main Schedule is required');
        setSaving(false);
        return;
      }
      if (!formData.contractedHours || formData.contractedHours === 0) {
        toast.error('Contracted Hours is required');
        setSaving(false);
        return;
      }
      if (!formData.baseRate || formData.baseRate === 0) {
        toast.error('Pay Rate is required');
        setSaving(false);
        return;
      }
      
      // Validate overtime fields if overtime is eligible
      if (formData.overtimeEligible) {
        if (!formData.ot1TriggerHours || formData.ot1TriggerHours === 0) {
          toast.error('OT1 Trigger Hours is required when Overtime Eligible is checked');
          setSaving(false);
          return;
        }
        if (!formData.ot1RateMultiplier || formData.ot1RateMultiplier === 0) {
          toast.error('OT1 Rate Multiplier is required when Overtime Eligible is checked');
          setSaving(false);
          return;
        }
        if (!formData.ot2TriggerHours || formData.ot2TriggerHours === 0) {
          toast.error('OT2 Trigger Hours is required when Overtime Eligible is checked');
          setSaving(false);
          return;
        }
        if (!formData.ot2RateMultiplier || formData.ot2RateMultiplier === 0) {
          toast.error('OT2 Rate Multiplier is required when Overtime Eligible is checked');
          setSaving(false);
          return;
        }
      }
      
      // Validate Project-specific fields if Pay Type is "Project"
      const paymentType = paymentTypes.find(pt => pt.id === formData.paymentTypeId);
      if (paymentType?.name === 'Project') {
        if (!formData.paymentFrequencyId) {
          toast.error('Pay Frequency is required when Pay Type is "Project"');
          setSaving(false);
          return;
        }
        if (!formData.projectStartDate) {
          toast.error('Project Start Date is required when Pay Type is "Project"');
          setSaving(false);
          return;
        }
        if (!formData.projectEndDate) {
          toast.error('Project End Date is required when Pay Type is "Project"');
          setSaving(false);
          return;
        }
        // Validate that Project End Date is after Project Start Date
        if (formData.projectStartDate && formData.projectEndDate) {
          const startDate = new Date(formData.projectStartDate);
          const endDate = new Date(formData.projectEndDate);
          if (endDate <= startDate) {
            toast.error('Project End Date must be after Project Start Date');
            setSaving(false);
            return;
          }
        }
      }
      
      // For active records, copy EffectiveFrom to FromDate when saving
      if (editingRecord) {
        const updateData: UpdatePayRecordRequest = {
          ...formData,
          workingDayIds: selectedWorkingDayIds.length > 0 ? selectedWorkingDayIds : undefined,
          payRateMatrices: payRateMatrices.length > 0 ? payRateMatrices : undefined,
        };
        
        if (formData.isActive && formData.effectiveFrom) {
          updateData.fromDate = formData.effectiveFrom;
        }
        
        await payRecordApi.update(editingRecord.id, updateData);
        toast.success('Pay record updated successfully!');
      } else {
        const createData: CreatePayRecordRequest = {
          ...formData,
          workingDayIds: selectedWorkingDayIds.length > 0 ? selectedWorkingDayIds : undefined,
          payRateMatrices: payRateMatrices.length > 0 ? payRateMatrices : undefined,
        };
        
        if (formData.isActive && formData.effectiveFrom) {
          createData.fromDate = formData.effectiveFrom;
        }
        
        await payRecordApi.create(createData);
        toast.success('Pay record created successfully!');
      }
      
      setShowPayRecordModal(false);
      setEditingRecord(null);
      resetForm();
      await onRefresh();
    } catch (error: any) {
      console.error('Error saving pay record:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save pay record. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: employee.id,
      locationId: employee.profile?.mainLocationId || 0,
      positionId: employee.profile?.mainPositionId || 0,
      scheduleId: 0,
      paymentTypeId: 1,
      contractedHours: 0,
      baseRate: 0,
      overtimeEligible: false,
      ot1TriggerHours: undefined,
      ot2TriggerHours: undefined,
      ot1RateMultiplier: undefined,
      ot2RateMultiplier: undefined,
      isActive: false,
      projectStartDate: undefined,
      projectEndDate: undefined,
    });
    setSelectedWorkingDayIds([]);
    setPayRateMatrices([]);
  };

  const handleEdit = (record: PayRecord) => {
    setEditingRecord(record);
    setFormData({
      employeeId: employee.id,
      fromDate: record.fromDate ? record.fromDate.split('T')[0] : undefined,
      toDate: record.toDate ? record.toDate.split('T')[0] : undefined,
      locationId: record.locationId ?? employee.profile?.mainLocationId ?? 0,
      scheduleId: record.scheduleId,
      positionId: record.positionId ?? employee.profile?.mainPositionId ?? 0,
      effectiveFrom: record.effectiveFrom ? record.effectiveFrom.split('T')[0] : undefined,
      isActive: record.isActive,
      paymentTypeId: record.paymentTypeId,
      paymentFrequencyId: record.paymentFrequencyId ?? undefined,
      contractedHours: record.contractedHours ?? 0,
      baseRate: record.baseRate ?? 0,
      hourlyRate: record.hourlyRate ?? undefined,
      sundayRateMultiplier: record.sundayRateMultiplier ?? undefined,
      bankHolidayRateMultiplier: record.bankHolidayRateMultiplier ?? undefined,
      nightShiftMultiplier: record.nightShiftMultiplier ?? undefined,
      overtimeEligible: record.overtimeEligible,
      overtimeTriggerHours: record.overtimeTriggerHours ?? undefined,
      overtimeRateMultiplier: record.overtimeRateMultiplier ?? undefined,
      ot1TriggerHours: record.ot1TriggerHours ?? undefined,
      ot2TriggerHours: record.ot2TriggerHours ?? undefined,
      ot1RateMultiplier: record.ot1RateMultiplier ?? undefined,
      ot2RateMultiplier: record.ot2RateMultiplier ?? undefined,
      assignLaborCostById: record.assignLaborCostById ?? undefined,
      workingDaysPerWeek: record.workingDaysPerWeek ?? undefined,
      dailyRate: record.dailyRate ?? undefined,
      weeklyRate: record.weeklyRate ?? undefined,
      calculatedHourlyRate: record.calculatedHourlyRate ?? undefined,
      // Backward compatibility
      sundayRate: record.sundayRate ?? undefined,
      bankHolidayRate: record.bankHolidayRate ?? undefined,
      overtimeRate: record.overtimeRate ?? undefined,
      // Project-specific fields
      projectStartDate: record.projectStartDate ? record.projectStartDate.split('T')[0] : undefined,
      projectEndDate: record.projectEndDate ? record.projectEndDate.split('T')[0] : undefined,
    });
    
    const workingDayIds = record.payRecordWorkingDays?.map(prwd => prwd.workingDayId) || [];
    setSelectedWorkingDayIds(workingDayIds);
    
    const matrices = (record.payRateMatrices || []).map(prm => ({
      displayOrder: prm.displayOrder,
      locationId: prm.locationId ?? undefined,
      positionId: prm.positionId ?? undefined,
      scheduleId: prm.scheduleId ?? undefined,
      payRate: prm.payRate,
    }));
    setPayRateMatrices(matrices);
    
    setShowPayRecordModal(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this pay record?')) {
      try {
        await payRecordApi.delete(id);
        await onRefresh();
        toast.success('Pay record deleted successfully!');
      } catch (error) {
        console.error('Error deleting pay record:', error);
        toast.error('Failed to delete pay record. Please try again.');
      }
    }
  };

  const addPayRateMatrixRow = () => {
    const newRow: CreatePayRateMatrixRequest = {
      displayOrder: payRateMatrices.length,
      locationId: undefined,
      positionId: undefined,
      scheduleId: undefined,
      payRate: 0,
    };
    setPayRateMatrices([...payRateMatrices, newRow]);
  };

  const removePayRateMatrixRow = (index: number) => {
    const updated = payRateMatrices.filter((_, i) => i !== index);
    // Reorder displayOrder
    updated.forEach((row, i) => {
      row.displayOrder = i;
    });
    setPayRateMatrices(updated);
  };

  const movePayRateMatrixRow = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === payRateMatrices.length - 1) return;
    
    const updated = [...payRateMatrices];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated[index].displayOrder = index;
    updated[newIndex].displayOrder = newIndex;
    setPayRateMatrices(updated);
  };

  const updatePayRateMatrixRow = (index: number, field: keyof CreatePayRateMatrixRequest, value: any) => {
    const updated = [...payRateMatrices];
    updated[index] = { ...updated[index], [field]: value };
    setPayRateMatrices(updated);
  };

  // Show weekly/monthly fields for Salary, Daily, and Project (not for Hourly)
  const isWeeklyOrMonthly = formData.paymentTypeId ? 
    !['Hourly'].includes(paymentTypes.find(pt => pt.id === formData.paymentTypeId)?.name || '') : false;
  const assignLaborCostBy = assignLaborCostByOptions.find(alcbo => alcbo.id === formData.assignLaborCostById);
  const showWorkingDays = assignLaborCostBy?.name === 'Pre Set Days';
  const showWorkingDaysPerWeek = assignLaborCostBy?.name === 'Daily Rate';
  // Show project-specific fields only when Payment Type is "Project"
  const isProject = formData.paymentTypeId ? 
    paymentTypes.find(pt => pt.id === formData.paymentTypeId)?.name === 'Project' : false;
  // Filter payment frequencies based on payment type
  // For Project: show only "Contract" and "Monthly Invoice"
  // For others: show regular frequencies (Weekly, Monthly, Fortnightly)
  const availablePaymentFrequencies = isProject
    ? paymentFrequencies.filter(pf => pf.name === 'Contract' || pf.name === 'Monthly Invoice')
    : paymentFrequencies.filter(pf => pf.name !== 'Contract' && pf.name !== 'Monthly Invoice');

  return (
    <div className="space-y-6">
      {/* Payroll Section */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-neutral-800">Payroll Information</h2>
          <button
            type="button"
            onClick={() => setShowPayrollModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Edit className="h-4 w-4" />
            Edit Payroll
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Works Number</label>
            <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
              {employee.payroll?.worksNumber || 'N/A'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">PPS Number</label>
            <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
              {employee.payroll?.ppsNumber || 'N/A'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">BAN</label>
            <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
              {employee.payroll?.ban || 'N/A'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">BIC</label>
            <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
              {employee.payroll?.bic || 'N/A'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Account Number</label>
            <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
              {employee.payroll?.accountNumber || 'N/A'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Sort Code</label>
            <div className="px-3 py-2 bg-neutral-50 border border-neutral-300 rounded-md">
              {employee.payroll?.sortCode || 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Pay Records Section */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-neutral-800">Pay Records</h2>
          <button
            type="button"
            onClick={() => {
              setEditingRecord(null);
              resetForm();
              setShowPayRecordModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Pay Record
          </button>
        </div>

        {payRecords.length === 0 ? (
          <p className="text-neutral-500">No pay records found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {payRecords.some(pr => pr.isActive) ? 'Effective From' : 'From Date'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {payRecords.some(pr => !pr.isActive) ? 'To Date' : ''}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Payment Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Pay Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {payRecords.map((record) => (
                  <tr key={record.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${record.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {record.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {record.isActive && record.effectiveFrom 
                        ? new Date(record.effectiveFrom).toLocaleDateString()
                        : record.fromDate 
                        ? new Date(record.fromDate).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {!record.isActive && record.toDate ? new Date(record.toDate).toLocaleDateString() : record.isActive ? '' : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.location?.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.position?.title || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.paymentType?.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">{record.baseRate || record.hourlyRate || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button onClick={() => handleEdit(record)} className="text-blue-600 hover:text-blue-900 mr-4">
                        <Edit className="h-4 w-4 inline" />
                      </button>
                      <button onClick={() => handleDelete(record.id)} className="text-red-600 hover:text-red-900">
                        <Trash2 className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payroll Edit Modal */}
      {showPayrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit Payroll Information</h3>
              <button onClick={() => setShowPayrollModal(false)} className="text-neutral-500 hover:text-neutral-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-neutral-500 mb-4">
              Payroll information is managed through the main employee form. Use the "Save Changes" button at the bottom of the page to save payroll updates.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowPayrollModal(false)}
                className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Record Modal - This will be continued in the next part due to size */}
      {showPayRecordModal && (
        <PayRecordModal
          formData={formData}
          setFormData={setFormData}
          selectedWorkingDayIds={selectedWorkingDayIds}
          setSelectedWorkingDayIds={setSelectedWorkingDayIds}
          payRateMatrices={payRateMatrices}
          setPayRateMatrices={setPayRateMatrices}
          addPayRateMatrixRow={addPayRateMatrixRow}
          removePayRateMatrixRow={removePayRateMatrixRow}
          movePayRateMatrixRow={movePayRateMatrixRow}
          updatePayRateMatrixRow={updatePayRateMatrixRow}
          editingRecord={editingRecord}
          saving={saving}
          onClose={() => {
            setShowPayRecordModal(false);
            setEditingRecord(null);
            resetForm();
          }}
          onSubmit={handlePayRecordSubmit}
          locations={employeeLocations}
          positions={employeePositions}
          schedules={employeeSchedules}
          paymentTypes={paymentTypes}
          paymentFrequencies={paymentFrequencies}
          assignLaborCostByOptions={assignLaborCostByOptions}
          workingDays={workingDays}
          isWeeklyOrMonthly={isWeeklyOrMonthly}
          showWorkingDays={showWorkingDays}
          showWorkingDaysPerWeek={showWorkingDaysPerWeek}
        />
      )}
    </div>
  );
};

// Pay Record Modal Component (separated for readability)
interface PayRecordModalProps {
  formData: CreatePayRecordRequest;
  setFormData: React.Dispatch<React.SetStateAction<CreatePayRecordRequest>>;
  selectedWorkingDayIds: number[];
  setSelectedWorkingDayIds: React.Dispatch<React.SetStateAction<number[]>>;
  payRateMatrices: CreatePayRateMatrixRequest[];
  setPayRateMatrices: React.Dispatch<React.SetStateAction<CreatePayRateMatrixRequest[]>>;
  addPayRateMatrixRow: () => void;
  removePayRateMatrixRow: (index: number) => void;
  movePayRateMatrixRow: (index: number, direction: 'up' | 'down') => void;
  updatePayRateMatrixRow: (index: number, field: keyof CreatePayRateMatrixRequest, value: any) => void;
  editingRecord: PayRecord | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  locations: Location[];
  positions: Position[];
  schedules: Schedule[];
  paymentTypes: PaymentType[];
  paymentFrequencies: PaymentFrequency[];
  assignLaborCostByOptions: AssignLaborCostByOption[];
  workingDays: WorkingDay[];
  isWeeklyOrMonthly: boolean;
  showWorkingDays: boolean;
  showWorkingDaysPerWeek: boolean;
}

const PayRecordModal: React.FC<PayRecordModalProps> = ({
  formData,
  setFormData,
  selectedWorkingDayIds,
  setSelectedWorkingDayIds,
  payRateMatrices,
  addPayRateMatrixRow,
  removePayRateMatrixRow,
  movePayRateMatrixRow,
  updatePayRateMatrixRow,
  editingRecord,
  saving,
  onClose,
  onSubmit,
  locations,
  positions,
  schedules,
  paymentTypes,
  paymentFrequencies,
  assignLaborCostByOptions,
  workingDays,
  isWeeklyOrMonthly,
  showWorkingDays,
  showWorkingDaysPerWeek,
}) => {
  // Show project-specific fields only when Payment Type is "Project"
  const isProject = formData.paymentTypeId ? 
    paymentTypes.find(pt => pt.id === formData.paymentTypeId)?.name === 'Project' : false;
  // Filter payment frequencies based on payment type
  // For Project: show only "Contract" and "Monthly Invoice"
  // For others: show regular frequencies (Weekly, Monthly, Fortnightly)
  const availablePaymentFrequencies = isProject
    ? paymentFrequencies.filter(pf => pf.name === 'Contract' || pf.name === 'Monthly Invoice')
    : paymentFrequencies.filter(pf => pf.name !== 'Contract' && pf.name !== 'Monthly Invoice');
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl my-8 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 z-10">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-neutral-800">{editingRecord ? 'Edit Pay Record' : 'Add Pay Record'}</h3>
              <p className="text-sm text-neutral-500 mt-1">Configure employee pay record details</p>
            </div>
            <button 
              onClick={onClose} 
              className="text-neutral-400 hover:text-neutral-600 transition-colors p-1 rounded-full hover:bg-neutral-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="px-6 py-6">
        
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Section 1: Record Status & Dates */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Record Status & Dates</h4>
              <p className="text-xs text-neutral-500 mt-1">Set the record status and effective date range</p>
            </div>
            
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive || false}
                  onChange={(e) => {
                    const isActive = e.target.checked;
                    setFormData({
                      ...formData,
                      isActive,
                      // When activating, copy EffectiveFrom to FromDate if it exists
                      fromDate: isActive && formData.effectiveFrom ? formData.effectiveFrom : formData.fromDate,
                      // When activating, clear ToDate
                      toDate: isActive ? undefined : formData.toDate,
                    });
                  }}
                  disabled={saving}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-neutral-700">Active Record (Latest record should be active)</span>
              </label>
            </div>

            {/* Date Fields - Conditional based on IsActive */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.isActive ? (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Effective From *</label>
                  <input
                    type="date"
                    required={formData.isActive}
                    value={formData.effectiveFrom || ''}
                    onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value, fromDate: e.target.value })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">From Date *</label>
                    <input
                      type="date"
                      required={!formData.isActive}
                      value={formData.fromDate || ''}
                      onChange={(e) => setFormData({ ...formData, fromDate: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={formData.toDate || ''}
                      onChange={(e) => setFormData({ ...formData, toDate: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section 2: Assignment Details */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Assignment Details</h4>
              <p className="text-xs text-neutral-500 mt-1">Select location, position, and schedule for this pay record</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Location *</label>
              <select
                required
                value={formData.locationId || ''}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value ? parseInt(e.target.value) : 0 })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value={0}>Select Location</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Position *</label>
              <select
                required
                value={formData.positionId || ''}
                onChange={(e) => setFormData({ ...formData, positionId: e.target.value ? parseInt(e.target.value) : 0 })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value={0}>Select Position</option>
                {positions.map((pos) => (
                  <option key={pos.id} value={pos.id}>{pos.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Main Schedule *</label>
              <select
                required
                value={formData.scheduleId}
                onChange={(e) => setFormData({ ...formData, scheduleId: parseInt(e.target.value) })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value={0}>Select Schedule</option>
                {schedules.map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>{schedule.name}</option>
                ))}
              </select>
            </div>
            </div>
          </div>

          {/* Section 3: Payment Configuration */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Payment Configuration</h4>
              <p className="text-xs text-neutral-500 mt-1">Configure payment type, frequency, hours, and base rate</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Pay Type *</label>
              <select
                required
                value={formData.paymentTypeId}
                onChange={(e) => setFormData({ ...formData, paymentTypeId: parseInt(e.target.value) })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {paymentTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>{pt.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Pay Frequency{isProject && ' *'}
              </label>
              <select
                required={isProject}
                value={formData.paymentFrequencyId || ''}
                onChange={(e) => setFormData({ ...formData, paymentFrequencyId: e.target.value ? parseInt(e.target.value) : undefined })}
                disabled={saving}
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <option value="">Select Payment Frequency</option>
                {availablePaymentFrequencies.map((pf) => (
                  <option key={pf.id} value={pf.id}>{pf.name}</option>
                ))}
              </select>
            </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Contracted Hours (per week) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  min="0.01"
                  value={formData.contractedHours || ''}
                  onChange={(e) => setFormData({ ...formData, contractedHours: e.target.value ? parseFloat(e.target.value) : 0 })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Pay Rate *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  min="0.01"
                  value={formData.baseRate || ''}
                  onChange={(e) => setFormData({ ...formData, baseRate: e.target.value ? parseFloat(e.target.value) : 0 })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Rate Multipliers */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Rate Multipliers</h4>
              <p className="text-xs text-neutral-500 mt-1">Configure multipliers for special rates (Sunday, Bank Holiday, Night Shift)</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Sunday Rate Multiplier</label>
              <input
                type="number"
                step="0.01"
                value={formData.sundayRateMultiplier || ''}
                onChange={(e) => setFormData({ ...formData, sundayRateMultiplier: e.target.value ? parseFloat(e.target.value) : undefined })}
                disabled={saving}
                placeholder="e.g., 1.5"
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Bank Holiday Rate Multiplier</label>
              <input
                type="number"
                step="0.01"
                value={formData.bankHolidayRateMultiplier || ''}
                onChange={(e) => setFormData({ ...formData, bankHolidayRateMultiplier: e.target.value ? parseFloat(e.target.value) : undefined })}
                disabled={saving}
                placeholder="e.g., 2.0"
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Night Shift Multiplier</label>
              <input
                type="number"
                step="0.01"
                value={formData.nightShiftMultiplier || ''}
                onChange={(e) => setFormData({ ...formData, nightShiftMultiplier: e.target.value ? parseFloat(e.target.value) : undefined })}
                disabled={saving}
                placeholder="e.g., 1.5"
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
            </div>
            </div>
          </div>

          {/* Section 5: Overtime Configuration */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <h4 className="text-base font-semibold text-neutral-800">Overtime Configuration</h4>
              <p className="text-xs text-neutral-500 mt-1">Configure overtime eligibility and OT1/OT2 rates</p>
            </div>
            
            <div>
              <label className="flex items-center mb-3">
                <input
                  type="checkbox"
                  checked={formData.overtimeEligible || false}
                  onChange={(e) => setFormData({ ...formData, overtimeEligible: e.target.checked })}
                  disabled={saving}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-neutral-700">Overtime Eligible</span>
              </label>
              {formData.overtimeEligible && (
                <div className="bg-white rounded-lg p-4 space-y-4 border border-blue-200">
                  <div>
                    <h5 className="text-sm font-semibold text-neutral-700 mb-3">OT1 Settings</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT1 Trigger Hours (per week) *</label>
                        <input
                          type="number"
                          step="0.01"
                          required={formData.overtimeEligible}
                          min="0.01"
                          value={formData.ot1TriggerHours || ''}
                          onChange={(e) => setFormData({ ...formData, ot1TriggerHours: e.target.value ? parseFloat(e.target.value) : 0 })}
                          disabled={saving}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT1 Rate Multiplier *</label>
                        <input
                          type="number"
                          step="0.01"
                          required={formData.overtimeEligible}
                          min="0.01"
                          value={formData.ot1RateMultiplier || ''}
                          onChange={(e) => setFormData({ ...formData, ot1RateMultiplier: e.target.value ? parseFloat(e.target.value) : 0 })}
                          disabled={saving}
                          placeholder="e.g., 1.5"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="text-sm font-semibold text-neutral-700 mb-3">OT2 Settings</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT2 Trigger Hours (per week) *</label>
                        <input
                          type="number"
                          step="0.01"
                          required={formData.overtimeEligible}
                          min="0.01"
                          value={formData.ot2TriggerHours || ''}
                          onChange={(e) => setFormData({ ...formData, ot2TriggerHours: e.target.value ? parseFloat(e.target.value) : 0 })}
                          disabled={saving}
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-1">OT2 Rate Multiplier *</label>
                        <input
                          type="number"
                          step="0.01"
                          required={formData.overtimeEligible}
                          min="0.01"
                          value={formData.ot2RateMultiplier || ''}
                          onChange={(e) => setFormData({ ...formData, ot2RateMultiplier: e.target.value ? parseFloat(e.target.value) : 0 })}
                          disabled={saving}
                          placeholder="e.g., 2.0"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 6: Salary/Daily/Project Specific Fields */}
          {isWeeklyOrMonthly && (
            <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
              <div className="border-b border-blue-200 pb-2">
                <h4 className="text-base font-semibold text-neutral-800">Salary/Daily/Project Payment Fields</h4>
                <p className="text-xs text-neutral-500 mt-1">Configure labor cost assignment and working days</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Assign Labor Cost By</label>
                <select
                  value={formData.assignLaborCostById || ''}
                  onChange={(e) => setFormData({ ...formData, assignLaborCostById: e.target.value ? parseInt(e.target.value) : undefined })}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  <option value="">Select Option</option>
                  {assignLaborCostByOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.name}</option>
                  ))}
                </select>
              </div>

              {showWorkingDaysPerWeek && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Working Days Per Week</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.workingDaysPerWeek || ''}
                    onChange={(e) => setFormData({ ...formData, workingDaysPerWeek: e.target.value ? parseFloat(e.target.value) : undefined })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
              )}

              {showWorkingDays && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Working Days</label>
                  <div className="grid grid-cols-2 gap-2">
                    {workingDays.map((day) => (
                      <label key={day.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedWorkingDayIds.includes(day.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedWorkingDayIds([...selectedWorkingDayIds, day.id]);
                            } else {
                              setSelectedWorkingDayIds(selectedWorkingDayIds.filter(id => id !== day.id));
                            }
                          }}
                          disabled={saving}
                          className="mr-2"
                        />
                        <span className="text-sm text-neutral-700">{day.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section 7: Project Specific Fields */}
          {isProject && (
            <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
              <div className="border-b border-blue-200 pb-2">
                <h4 className="text-base font-semibold text-neutral-800">Project Payment Fields</h4>
                <p className="text-xs text-neutral-500 mt-1">Set project start and end dates</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Project Start Date *</label>
                  <input
                    type="date"
                    required={isProject}
                    value={formData.projectStartDate || ''}
                    onChange={(e) => setFormData({ ...formData, projectStartDate: e.target.value || undefined })}
                    disabled={saving}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Project End Date *</label>
                  <input
                    type="date"
                    required={isProject}
                    value={formData.projectEndDate || ''}
                    onChange={(e) => setFormData({ ...formData, projectEndDate: e.target.value || undefined })}
                    disabled={saving}
                    min={formData.projectStartDate || undefined}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Section 8: Pay Rate Override Matrix */}
          <div className="space-y-4 bg-blue-50 rounded-lg p-5 border border-blue-100">
            <div className="border-b border-blue-200 pb-2">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-base font-semibold text-neutral-800">Pay Rate Override Matrix</h4>
                  <p className="text-xs text-neutral-500 mt-1">Configure location/position/schedule-specific pay rate overrides</p>
                </div>
                <button
                  type="button"
                  onClick={addPayRateMatrixRow}
                  className="flex items-center gap-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4" />
                  Add Row
                </button>
              </div>
              <p className="text-xs text-neutral-500 italic mt-2">
                Leave Location, Position, or Schedule blank to use as a wildcard (applies to all). At least one of these fields must have a value.
              </p>
            </div>
            
            {payRateMatrices.length === 0 ? (
              <p className="text-sm text-neutral-500">No pay rate override matrix rows. Click "Add Row" to add one.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Order</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Location</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Position</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Schedule</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Pay Rate (Hourly) *</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-neutral-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {payRateMatrices.map((row, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => movePayRateMatrixRow(index, 'up')}
                              disabled={index === 0}
                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => movePayRateMatrixRow(index, 'down')}
                              disabled={index === payRateMatrices.length - 1}
                              className="disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <select
                            value={row.locationId || ''}
                            onChange={(e) => updatePayRateMatrixRow(index, 'locationId', e.target.value ? parseInt(e.target.value) : undefined)}
                            disabled={saving}
                            className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            <option value="">All Locations</option>
                            {locations.map((loc) => (
                              <option key={loc.id} value={loc.id}>{loc.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <select
                            value={row.positionId || ''}
                            onChange={(e) => updatePayRateMatrixRow(index, 'positionId', e.target.value ? parseInt(e.target.value) : undefined)}
                            disabled={saving}
                            className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            <option value="">All Positions</option>
                            {positions.map((pos) => (
                              <option key={pos.id} value={pos.id}>{pos.title}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <select
                            value={row.scheduleId || ''}
                            onChange={(e) => updatePayRateMatrixRow(index, 'scheduleId', e.target.value ? parseInt(e.target.value) : undefined)}
                            disabled={saving}
                            className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                          >
                            <option value="">All Schedules</option>
                            {schedules.map((schedule) => (
                              <option key={schedule.id} value={schedule.id}>{schedule.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <input
                            type="number"
                            step="0.01"
                            required
                            value={row.payRate}
                            onChange={(e) => updatePayRateMatrixRow(index, 'payRate', parseFloat(e.target.value) || 0)}
                            disabled={saving}
                            className="w-full px-2 py-1 text-sm border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => removePayRateMatrixRow(index)}
                            disabled={saving}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="sticky bottom-0 bg-white border-t border-neutral-200 px-6 py-4 -mx-6 -mb-6 mt-8">
            <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? 'Saving...' : (editingRecord ? 'Update' : 'Create')}
            </button>
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default PayrollAndPayRecords;
